import { Button, Divider, Input, Modal, Spin } from "antd";
import React, { useState } from "react";
import "./SignIn.scss";
import {
  EyeInvisibleOutlined,
  EyeTwoTone,
  MailOutlined,
  SwapLeftOutlined,
} from "@ant-design/icons";
import googleLogo from "@assets/icons/google.png";
import { useGoogleLogin } from "@react-oauth/google";
import { Link, useNavigate } from "react-router-dom";
import Helpers from "@utils/Helpers";
import { useUser } from "contexts/UserContext";
import { useAuth } from "contexts/AuthContext";
import userApi from "services/UserApi";
import { useGallery } from "contexts/GalleryContext";
import { errorLog } from "logging";

const LOGIN_MESSAGE_CASES = {
  USER_NOT_FOUND: "User not found",
  WRONG_PASSWORD: "Wrong password",
  USER_INVALID: "User not verified",
};

const SignIn: React.FC = () => {
  const { updateUserProfile, fetchAllAddress } = useUser();
  const { fetchtGallery } = useGallery();
  const { login, setLoading } = useAuth();
  const navigate = useNavigate();
  const [signInForm, setSignInForm] = useState({
    email: "",
    password: "",
  });

  const validateForm = () => {
    if (signInForm.email == "" || signInForm.password == "") {
      return false;
    }
    return true;
  };

  const googleLoginHook = useGoogleLogin({
    onSuccess: async (codeResponse: any) => {
      if (codeResponse?.code) {
        googleSignIn(codeResponse.code);
      }
    },
    flow: "auth-code",
    onError: (err) => {
      Helpers.notification.error(
        "Can not login via Google. Please log in manually"
      );
    },
  });

  const googleSignIn = async (code: string) => {
    setLoading(true);
    try {
      const response = await userApi.googleSignIn({ code });
      if (response.status == 200) {
        if (response.data.message == "Login successfully") {
          handleLoginSuccess(
            response.data.data.access_token,
            response.data.data.refresh_token
          );
        } else {
          if (response.data.message == "User does not exist") {
            const data = response.data.data;
            Helpers.redirect(
              `/auth/signup?googleId=${data?.google_id || ""}&first_name=${data?.first_name || ""}&last_name=${data?.last_name || ""}&avatar=${data?.avatar || ""}&email=${data?.email || ""}`
            );
          }
        }
      }
      setLoading(false);
    } catch (error) {
      setLoading(false);
      Helpers.notification.error("Something wrong in server");
      errorLog("SignIn.tsx - googleSignIn", error);
    }
  };

  const SignIn = async () => {
    if (!validateForm()) {
      Helpers.notification.warning("PLease fill out the required fields");
      return;
    }
    setLoading(true);
    try {
      const response = await userApi.signIn(signInForm);
      if (response.status == 200) {
        if (response.data.message === LOGIN_MESSAGE_CASES.USER_NOT_FOUND) {
          Helpers.notification.warning("Your account doesn't exist");
          setLoading(false);
          return;
        } else if (
          response.data.message === LOGIN_MESSAGE_CASES.WRONG_PASSWORD
        ) {
          Helpers.notification.warning("Wrong password. Please try again");
          setLoading(false);
          return;
        } else if (response.data.message === LOGIN_MESSAGE_CASES.USER_INVALID) {
          openVerifyMessageModal();
          return;
        }
        handleLoginSuccess(
          response.data.data.access_token,
          response.data.data.refresh_token
        );
      }
    } catch (error) {
      Helpers.notification.error("Something wrong in server. Please try again");
      errorLog("SignIn.tsx - SignIn", error);
    }
  };

  const handleLoginSuccess = async (
    accessToken: string,
    refreshToken: string
  ) => {
    login();
    updateUserProfile(accessToken, refreshToken);
    fetchtGallery();
    fetchAllAddress();
    setLoading(false);
  };

  const openVerifyMessageModal = () => {
    Modal.warning({
      title: "Verify",
      content:
        "Your account has not been verified yet. Please verify first before join our community",
      onOk: () => {
        navigate(`/auth/signup?targetEmail=${signInForm.email}`);
      },
      closeIcon: false,
      okText: "Verify now",
      className: "verify-modal",
      okButtonProps: {
        icon: <MailOutlined />,
      },
    });
  };

  return (
    <div className="SignIn">
      <div className="SignIn-wrap">
        <Button
          className="back-btn"
          variant="link"
          color="default"
          icon={<SwapLeftOutlined />}
          onClick={() => Helpers.redirect("/")}
        >
          Back To Home
        </Button>
        <div className="signin-title">Login</div>
        <Button
          onClick={() => googleLoginHook()}
          className="google-login-btn"
          icon={<img src={googleLogo} />}
        >
          Login with Google
        </Button>
        <div className="divider-wrap">
          <Divider className="signin-divider" plain>
            or Log In with email
          </Divider>
        </div>
        <div className="forn-group mb-3">
          <div className="title">Email</div>
          <Input
            className="ant-form"
            variant="filled"
            size="large"
            value={signInForm.email}
            onChange={(e) =>
              setSignInForm({ ...signInForm, email: e.target.value })
            }
          />
        </div>
        <div className="forn-group">
          <div className="title">Password</div>
          <Input.Password
            variant="filled"
            className="ant-form"
            onKeyDown={(e) => {
              if (e.key == "Enter") SignIn();
            }}
            iconRender={(visible) =>
              visible ? (
                <EyeTwoTone twoToneColor="#79ad7f" />
              ) : (
                <EyeInvisibleOutlined />
              )
            }
            value={signInForm.password}
            onChange={(e) =>
              setSignInForm({ ...signInForm, password: e.target.value })
            }
          />
        </div>
        <Link to="/" className="forgot-pwd">
          Forgot password?
        </Link>
        <Button className="signin-btn" onClick={SignIn}>
          Log In
        </Button>
        <div className="signup-btn">
          Don't have an account? <Link to="signup">Sign Up</Link>
        </div>
      </div>
    </div>
  );
};

export default SignIn;
